import {
  MeetingNoteStatus,
  useCalendarEventMeetingNote,
} from 'src/meetings/hooks/useCalendarEventMeetingNote';
import { useCalendarEventMeetingAgenda } from 'src/meetings/hooks/useCalendarEventMeetingAgenda';
import { useGenerateCalendarEventMeetingNote } from 'src/meetings/hooks/useGenerateCalendarEventMeetingNote';
import { useSetMeetingNoteReviewed } from 'src/meetings/hooks/useSetMeetingNoteReviewed';
import { MeetingNoteTab } from 'src/meetings/detail/tabs/MeetingNoteTab';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import type { CalendarEvent } from 'src/meetings/models';
import type { Transcript } from 'src/meetings/detail/tabs/transcript/types';
import type { MeetingScope } from 'src/meetings/scope';
import { ReviewableMeetingNote } from './ReviewableMeetingNote';

type Props = {
  event: CalendarEvent;
  transcript: Transcript | undefined;
  scope: MeetingScope;
};

export function FamilyMeetingNoteTab({ event, transcript, scope }: Props) {
  const { meetingNote, refetch } = useCalendarEventMeetingNote(event.id, {
    enabled: event.hasTranscription,
  });
  const { generateMeetingNote, isLoading: isRegenerating } = useGenerateCalendarEventMeetingNote(
    event.id,
  );
  const { setMeetingNoteReviewed, isLoading: isSettingReviewed } = useSetMeetingNoteReviewed(
    event.id,
  );
  const { meetingAgenda } = useCalendarEventMeetingAgenda({
    eventId: event.id,
    enabled: event.hasMeetingAgenda,
  });

  const templateId = meetingNote?.templateId;
  const agendaFileId = meetingNote?.agendaFileId;
  const meetingAgendaId = meetingNote?.meetingAgendaId ?? meetingAgenda?.id;
  const canRegenerate = templateId != null || agendaFileId != null || meetingAgendaId != null;
  const meetingNoteId = meetingNote?.id;
  const clientId = meetingNote?.clientId;

  const handleRegenerate = async () => {
    try {
      if (templateId != null) {
        await generateMeetingNote({ templateId, summarise: true });
      } else if (agendaFileId != null) {
        await generateMeetingNote({ agendaFileId, summarise: true });
      } else if (meetingAgendaId != null) {
        await generateMeetingNote({ meetingAgendaId, summarise: true });
      } else {
        return;
      }
      refetch();
    } catch (error) {
      notifyAndLogError('Failed to regenerate meeting note', 'Please try again later', error, {
        eventId: event.id,
        templateId,
        agendaFileId,
        meetingAgendaId,
      });
    }
  };

  const handleToggleReviewed = async (isReviewed: boolean) => {
    if (!meetingNoteId || !clientId) return;
    try {
      await setMeetingNoteReviewed({ clientId, meetingNoteId, isReviewed });
    } catch (error) {
      notifyAndLogError('Failed to update review status', 'Please try again later', error, {
        eventId: event.id,
        meetingNoteId,
      });
    }
  };

  if (meetingNote?.status === MeetingNoteStatus.READY && meetingNote.markdownContent) {
    return (
      <ReviewableMeetingNote
        title={event.title}
        markdownContent={meetingNote.markdownContent}
        onRegenerate={canRegenerate ? handleRegenerate : undefined}
        isRegenerating={isRegenerating}
        isReviewed={meetingNote.isReviewed ?? false}
        onToggleReviewed={meetingNoteId && clientId ? handleToggleReviewed : undefined}
        isSettingReviewed={isSettingReviewed}
      />
    );
  }

  return <MeetingNoteTab event={event} transcript={transcript} scope={scope} />;
}
